import { Observable, Subject } from 'rxjs';
import { debounceTime, take, takeUntil, tap } from 'rxjs/operators';

import {
  Input,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { t } from '@lib/translation';
import { SnDataListComponent } from '@lib/ui/data-list';
import { SnMenuItemConfig } from '@lib/ui/menu';

import { EditMemberExpirationComponent } from '../../components/dialogs/edit-member-expiration/edit-member-expiration.component';
import { SnChatUser, SnChannel } from '../../models';
import { SnMembersService, SnChatService, SnChannelsService } from '../../services';

@Component({
  selector: 'sn-channel-members-list',
  templateUrl: './channel-members-list.container.html',
  styleUrls: ['./channel-members-list.container.scss'],
})
export class SnChannelMembersListContainer implements OnInit, OnDestroy {
  @ViewChild(SnDataListComponent, { static: true })
  public readonly list: SnDataListComponent<SnChatUser>;

  public readonly loadData = this._loadData.bind(this);

  public readonly canAdd: boolean;
  public readonly canDelete: boolean;

  public readonly memberActions: SnMenuItemConfig<SnChatUser>[] = [
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-envelope',
      },
      label: t('common.directMessages'),
      click: (item) => {
        this._openDirectMessages(item);
      },
    },
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-trash-alt',
      },
      label: t('communication.chat.members.removeUserFromChannel'),
      click: (item) => {
        this._removeUser(item);
      },
      hidden: () => !this.canDelete,
    },
    {
      icon: {
        fontSet: 'fal',
        fontIcon: 'fa-calendar-clock',
      },
      label: t('communication.chat.members.editExpiration'),
      click: (item) => {
        this._editExpiration(item);
      },
      hidden: (user) => !user.isExternal,
    },
  ];

  @Input()
  public sid: string;

  @Output()
  public readonly reloadChat = new EventEmitter<boolean>();

  public members: SnChatUser[] = [];
  public currentList: SnChatUser[] = [];
  public creatingChannel: boolean;
  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  private _querySearch: string;

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _membersService: SnMembersService,
    private readonly _chatService: SnChatService,
    private readonly _channelsService: SnChannelsService,
    private readonly _router: Router,
    private readonly _matDialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    ability: SnAbilityService,
  ) {
    const subject = 'communications/chatuser';

    this.canAdd = ability.can({ subject, action: 'add' });
    this.canDelete = ability.can({ subject, action: 'delete' });
  }
  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  private _openDirectMessages(user: SnChatUser): void {
    const targetChannel = this._chatService.channels.find((channelItem) => {
      return channelItem.pair === user.id;
    });
    if (targetChannel) {
      this._router.navigate(['/', 'chat', targetChannel.sid]);
    } else {
      this.creatingChannel = true;
      this._channelsService
        .createPairChannel(user.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe((channel: SnChannel) => {
          this._goToNewChannel(channel);
        });
    }
  }

  private _goToNewChannel(channel: SnChannel): void {
    this._chatService.channelsUpdated$.pipe(take(1), takeUntil(this._destroy$)).subscribe(() => {
      this._router.navigate(['/', 'chat', channel.sid]);
    });
  }

  private _removeUser(user: SnChatUser): void {
    this._membersService
      .removeUser(this.sid, user.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.list.reference.reload();
        this.reloadChat.emit(true);
        this._cd.detectChanges();
      });
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnChatUser>> {
    params.search = this._querySearch;

    return this._membersService.fetchMemberList(this.sid, params).pipe(
      tap((response) => {
        response.results.forEach((chatUser) => {
          this._chatService
            .getTwilioUser(chatUser.sid)
            .pipe(takeUntil(this._destroy$))
            .subscribe((twilioUser) => {
              chatUser.isOnline = twilioUser.isOnline;
            });
        });
      }),
      takeUntil(this._destroy$),
    );
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _editExpiration(user: SnChatUser): void {
    this._matDialog
      .open(EditMemberExpirationComponent, {
        data: {
          sid: this.sid,
          user,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          user.expirationTimestamp = result;
        }
        this._cd.detectChanges();
      });
  }
}
